import React, { useEffect, useState } from 'react'
import './expanded-job-view.scss'
import classnames from 'classnames'
import Icon, { Loading3QuartersOutlined } from '@ant-design/icons'
import { Descriptions, Button } from 'antd'
import InformedDeliveryJobView from '@halo/components/jobs-list/job-views/informed-delivery.job-view'
import { IExpandedJobView } from '@halo/components/jobs-list/interfaces'
import JsonView from '@halo/components/json-view/json-view'
import { Well } from '@pushly/aqe/lib/components'
import OpsGenieIcon from '@halo/components/icons/opsgenie-icon'
import { fetchJobOpsGenieId } from '@halo/api/jobs.api'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { useLoadableDataRefState } from '@halo/hooks/use-loadable-data-ref-state'
import { useMountedRef } from '@halo/hooks/use-mounted-ref'

interface IJobViewConfig {
    identifier: RegExp
    component: (props: IExpandedJobView) => JSX.Element
}
const jobViews: IJobViewConfig[] = [
    {
        identifier: /informed delivery\. domain: (?<domainId>[\d]+)\. notification: (?<notificationId>[\d+])/i,
        component: InformedDeliveryJobView,
    },
    // {
    //     identifier: /recurring trigger\. domain: (?<domainId>[\d]+)\. campaign: (?<campaignId>[\d+])/i,
    //     component: InformedDeliveryJobView,
    // },
    // {
    //     identifier: /ecomm trigger\. domain: (?<domainId>[\d]+)/i,
    //     component: InformedDeliveryJobView,
    // },
]

interface IJobButton {
    job: any
}

const OpsGenieButton = ({ job: { id, invocation_context } }: IJobButton) => {
    const runIfMounted = useMountedRef()[1]

    const opsGenieId = invocation_context.opsgenie_id
    const opsGenieRequestId = invocation_context.opsgenie_request_id

    const [opsIdRef, setOpsId] = useLoadableDataRefState({ loading: !opsGenieId, data: opsGenieId })
    useEffect(() => {
        if (!opsGenieId && !!opsGenieRequestId) {
            setOpsId({ loading: true })

            fetchJobOpsGenieId(id, {}).then((res: any) => {
                runIfMounted(() => {
                    setOpsId({ loading: false, data: res.data.opsgenie_id })
                })
            })
        }
    }, [false])

    return (
        <Button
            className="opsgenie-link"
            size="small"
            onClick={
                !opsIdRef.current.data
                    ? undefined
                    : () => {
                          window.open(`https://pushly.app.opsgenie.com/alert/detail/${opsIdRef.current.data}/details`)
                      }
            }
        >
            {opsIdRef.current.loading ? <Loading3QuartersOutlined spin={true} /> : <Icon component={OpsGenieIcon} />}
            <span>OpsGenie</span>
        </Button>
    )
}

const AwsBatchButton = ({ job: { invocation_context } }: IJobButton) => {
    const regionRgx = /^arn:aws:batch:(?<region>[^:]+)/i
    const region = regionRgx.exec(invocation_context.aws_batch_job_arn)?.groups?.region ?? 'us-east-1'
    const batchId = invocation_context.aws_batch_job_id

    return (
        <Button
            className="batch-link"
            size="small"
            onClick={() => {
                window.open(`https://console.aws.amazon.com/batch/v2/home?region=${region}#jobs/detail/${batchId}`)
            }}
        >
            <span>AWS Batch</span>
        </Button>
    )
}

const AwsInsightsButton = ({ job }: IJobButton) => {
    const appState = useService<AppState>(AppState)
    const context = job.invocation_context
    const reqId = context.aws_request_id
    const batchArn = context.aws_batch_job_arn

    let region = 'us-east-1'
    if (!!batchArn) {
        const regionRgx = /^arn:aws:batch:(?<region>[^:]+)/i
        region = regionRgx.exec(context.aws_batch_job_arn)?.groups?.region ?? region
    }

    /**
     * AWS Insights querystring delimiters
     * *20      \s
     * *40      @
     * *2c      ,
     * *0a      \n
     * *7c      |
     * *3d      =
     * *27      '
     * *2f      /
     * *3a      :
     *
     * $3F      ??? - start
     * $3D      ??? - post start
     * $257E    ??? - quote-like usage
     * $2527    ???
     * $2528    ???
     * $2529    ??? - final
     *
     * Example:
     *     $257EeditorString$257E$2527fields*20*40timestamp*2c*20*40message*2c*20*40requestId*0a*7c*20filter*20*40requestId*20*3d*20*27${reqId}*27*0a*7c*20sort*20*40timestamp*20desc*0a*7c*20limit*20100
     *     "editorString"?
     *     fields @timetamp, @message, @requestId
     *     | filter @requestId = '${reqId}'
     *     | sort @timestamp desc
     *     | limit 100
     *
     *     $257Esource$257E$2528$257E$2527*2faws*2fbatch*2fjob$257E$2527*2faws*2flambda*2fsubmarine-communications-cable$2529$2529
     *     "source":"?/aws/batch/seaworld-normal-1g"?/aws/lambda/submarine-communications-cable}}
     */
    const getSrc = (src: string[]) => `$257E$2527*2faws*2f${src.join('*2f')}`

    const root = `https://console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:logs-insights$3FqueryDetail$3D$257E$2528`
    const end = '$2529$2529'

    const dtEnd = new Date() // now
    const dtStart = new Date(job.created_at)
    dtStart.setHours(0)
    dtStart.setMinutes(0)
    dtStart.setSeconds(0)

    const dtStartStr = dtStart.toISOString().replace(/:/g, '*3a')
    const dtEndStr = dtEnd.toISOString().replace(/:/g, '*3a')

    const timeEnd = `end$257E$2527${dtEndStr}$257E`
    const timeStart = `start$257E$2527${dtStartStr}$257E`
    // const timeTz = appState.displayTimeZone === 'UTC' ? 'UTC' : 'Local'; // TODO: add tz switch support
    const timeType = `timeType$257E$2527ABSOLUTE$257Etz$257E$2527UTC$257E`
    const timeStr = `${timeEnd}${timeStart}${timeType}`

    let editorStr: string
    let srcStr: string
    if (!!batchArn) {
        // | filter @message like /{job_id}/\n
        const filter = `*7c*20filter*20*40message*20like*20*2f${job.id}*2f*0a`
        editorStr = `editorString$257E$2527fields*20*40timestamp*2c*20*40message*2c*20*40requestId*0a${filter}*7c*20sort*20*40timestamp*20desc*0a*7c*20limit*20100$257E`
        srcStr = `source$257E$2528${getSrc(['batch', 'job'])}`
    } else {
        // | filter @requestId = '{request_id}'\n
        const filter = `*7c*20filter*20*40requestId*20*3d*20*27${reqId}*27*0a`
        editorStr = `editorString$257E$2527fields*20*40timestamp*2c*20*40message*2c*20*40requestId*0a${filter}*7c*20sort*20*40timestamp*20desc*0a*7c*20limit*20100$257E`
        srcStr = `source$257E$2528${getSrc([job.target_config.type, job.target_config.name])}`
    }

    return (
        <Button
            className="insights-link"
            size="small"
            onClick={() => {
                window.open(`${root}${timeStr}${editorStr}${srcStr}${end}`)
            }}
        >
            <span>AWS Insights</span>
        </Button>
    )
}

const ExpandedJobView = ({ job, index, expanded, indent }: IExpandedJobView) => {
    const appState = useService<AppState>(AppState)
    const currentUserIsInternalDevUser = appState.currentUser?.isInternalDevUser

    const { type, name, ...targetConfig } = job.target_config ?? {}
    const nestedView = jobViews.find((jv) => jv.identifier.test(job.name))

    const wellActions: JSX.Element[] = []
    if (currentUserIsInternalDevUser) {
        const opsGenieId = job.invocation_context?.opsgenie_id
        const opsGenieRequestId = job.invocation_context?.opsgenie_request_id
        if (opsGenieId || opsGenieRequestId) {
            wellActions.push(<OpsGenieButton key="opsgenie" job={job} />)
        }

        const batchArn = job.invocation_context?.aws_batch_job_arn
        const batchId = job.invocation_context?.aws_batch_job_id
        if (batchId && batchArn) {
            wellActions.push(<AwsBatchButton key="batch" job={job} />)
        }

        const awsRequestId = job.invocation_context?.aws_request_id
        if (awsRequestId) {
            wellActions.push(<AwsInsightsButton key="insights" job={job} />)
        }
    }

    return (
        <div className={classnames('expanded-job-view')}>
            <Well title="Job Details" actions={wellActions} hideFooter={true}>
                <Descriptions layout="horizontal" column={1}>
                    <Descriptions.Item label="Name">
                        <span>{job.name}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Job ID">
                        <span>{job.id}</span>
                        {!job.cloned_from_job_id ? undefined : (
                            <span>
                                {' '}
                                ( <b>Cloned</b> from <span className="clone-id">{job.cloned_from_job_id} )</span>
                            </span>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Schedule ID">
                        <span>{job.job_schedule_id ?? '--'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Target">
                        <span className="target-type">[{type}] </span>
                        <span className="target-name">{name}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Target Config">
                        <JsonView data={targetConfig} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Alarm Config">
                        <JsonView data={job.alarm_config ?? null} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Payload">
                        <JsonView data={job.payload ?? null} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Result">
                        <JsonView data={job.result ?? null} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Invocation">
                        <JsonView data={job.invocation_context ?? {}} />
                    </Descriptions.Item>
                </Descriptions>
            </Well>

            {!!nestedView?.component && (
                <nestedView.component job={job} index={index} expanded={expanded} indent={indent} />
            )}
        </div>
    )
}

export default ExpandedJobView
